import React, { useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { createHourLogEntry, updateHourLogEntry } from "../../lib/api";
import { todayIso } from "./utils";

/**
 * Triple-purpose dialog:
 *   - ``entry`` ............ existing row → PATCH (single employee)
 *   - ``draft`` ............ {employee_id, date} → CREATE one entry
 *   - ``draft.employees`` .. array → multi-select picker. The user
 *     toggles checkboxes, fills in the start/end/address/desc ONCE,
 *     and the dialog fans out and creates one identical entry per
 *     selected employee. Initial selection seeds from
 *     ``draft.employee_id`` when present so per-employee quick-add
 *     buttons still work as before. (iter-115)
 */
export default function EntryEditDialog({ entry, draft, onClose, onSaved }) {
    const isCreate = !entry;
    const date = entry?.date || draft?.date || todayIso();
    const employeeChoices = (draft?.employees || []).filter(Boolean);
    const multiMode = isCreate && employeeChoices.length > 0;

    const [selectedIds, setSelectedIds] = useState(() => {
        if (!isCreate) return [];
        if (draft?.employee_id) return [draft.employee_id];
        return [];
    });

    const [startTime, setStartTime] = useState(
        entry ? (entry.start_time || "").slice(0, 16) : `${date}T09:00`,
    );
    const [endTime, setEndTime] = useState(
        entry ? (entry.end_time || "").slice(0, 16) : `${date}T17:00`,
    );
    const [addr, setAddr] = useState(entry?.job_address || "");
    const [desc, setDesc] = useState(entry?.job_description || "");
    const [busy, setBusy] = useState(false);

    const toggleEmp = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };
    const selectAll = () => {
        setSelectedIds(employeeChoices.map((e) => e.id));
    };
    const clearAll = () => setSelectedIds([]);

    const save = async () => {
        if (!startTime || !endTime) {
            toast.error("Start and end time are required");
            return;
        }
        const startIso = new Date(startTime).toISOString();
        const endIso = new Date(endTime).toISOString();
        setBusy(true);
        try {
            if (isCreate) {
                const ids = multiMode
                    ? selectedIds
                    : (draft?.employee_id ? [draft.employee_id] : []);
                if (ids.length === 0) {
                    toast.error("Pick at least one employee");
                    setBusy(false);
                    return;
                }
                // Fan-out in parallel — every entry is independent so a
                // failure on one doesn't block the others. We surface
                // any failures as a single toast at the end.
                const results = await Promise.allSettled(
                    ids.map((employee_id) => createHourLogEntry({
                        employee_id,
                        date,
                        start_time: startIso,
                        end_time: endIso,
                        job_address: addr,
                        job_description: desc,
                    })),
                );
                const failed = results.filter((r) => r.status === "rejected").length;
                const ok = results.length - failed;
                if (ok > 0) {
                    toast.success(
                        ok === 1
                            ? "Hours added"
                            : `Hours added for ${ok} employee${ok === 1 ? "" : "s"}`,
                    );
                }
                if (failed > 0) {
                    toast.error(
                        `Couldn't add hours for ${failed} employee${failed === 1 ? "" : "s"}`,
                    );
                }
            } else {
                await updateHourLogEntry(entry.id, {
                    start_time: startTime ? startIso : null,
                    end_time: endTime ? endIso : null,
                    job_address: addr,
                    job_description: desc,
                });
            }
            await onSaved();
        } catch { toast.error("Couldn't save"); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={!!(entry || draft)} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="bg-[#131A2F] border border-[#2A3655] text-white max-w-md max-h-[90vh] overflow-y-auto"
                data-testid="entry-edit-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-white">
                        {isCreate ? "Add hours" : "Edit hours"}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    {multiMode && (
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="text-[10px] uppercase font-bold text-slate-400">
                                    Employees · {selectedIds.length} selected
                                </label>
                                <div className="flex gap-1 text-[10px] uppercase tracking-wider font-bold">
                                    <button
                                        type="button"
                                        onClick={selectAll}
                                        data-testid="entry-select-all"
                                        className="text-[#00A3FF] hover:text-white px-1.5 py-0.5"
                                    >All</button>
                                    <button
                                        type="button"
                                        onClick={clearAll}
                                        data-testid="entry-clear-all"
                                        className="text-slate-400 hover:text-rose-400 px-1.5 py-0.5"
                                    >None</button>
                                </div>
                            </div>
                            <div
                                className="bg-[#0A1128] border border-[#2A3655] rounded-sm max-h-40 overflow-y-auto divide-y divide-[#2A3655]/60"
                                data-testid="entry-employee-picker"
                            >
                                {employeeChoices.map((emp) => {
                                    const on = selectedIds.includes(emp.id);
                                    return (
                                        <label
                                            key={emp.id}
                                            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[#1C253C]"
                                            data-testid={`entry-emp-${emp.id}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={on}
                                                onChange={() => toggleEmp(emp.id)}
                                                className="accent-[#00A3FF]"
                                                data-testid={`entry-emp-checkbox-${emp.id}`}
                                            />
                                            <span className="text-sm text-white truncate">{emp.name}</span>
                                            {emp.hourly_rate != null && (
                                                <span className="ml-auto text-[10px] text-emerald-300 font-mono">
                                                    ${Number(emp.hourly_rate).toFixed(2)}/h
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="text-[10px] text-slate-500 mt-1">
                                One identical entry will be created for each selected employee.
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400">Start</label>
                        <Input type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="entry-start" className="bg-[#0A1128] border-[#2A3655] mt-1" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400">End</label>
                        <Input type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="entry-end" className="bg-[#0A1128] border-[#2A3655] mt-1" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400">Job address</label>
                        <Input value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="entry-address" className="bg-[#0A1128] border-[#2A3655] mt-1" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400">Job description</label>
                        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="entry-description" className="bg-[#0A1128] border-[#2A3655] min-h-[60px] mt-1" />
                    </div>
                </div>
                <DialogFooter>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-xs uppercase font-bold rounded-sm border border-[#2A3655] text-slate-300 hover:text-white"
                    >Cancel</button>
                    <button
                        onClick={save}
                        disabled={busy || (multiMode && selectedIds.length === 0)}
                        data-testid="btn-save-entry"
                        className="px-4 py-2 text-xs uppercase font-bold rounded-sm bg-[#00A3FF]/20 border border-[#00A3FF]/50 text-[#00A3FF] hover:bg-[#00A3FF]/30 disabled:opacity-50"
                    >
                        {busy
                            ? "Saving…"
                            : isCreate
                                ? (multiMode && selectedIds.length > 1
                                    ? `Add to ${selectedIds.length} employees`
                                    : "Add")
                                : "Save"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
